import { COMPETITION_SETUP_ROLES, EVENT_RECORDS_PAGE_SIZE_DEFAULT, type EventRecordItem, type RoleCode, type SportOption } from "@seagames/contracts";
import { useFilterDraft } from "@seagames/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { PAGE_SIZE_CHOICES, pageBeyondEnd, pageToUrl, sizeToUrl } from "../../components/nocs/list-params";
import { useOnGamesSwitch, useScrollToTopOnPage } from "../../components/nocs/use-list-page";
import { FilterPanel } from "../../components/page/filter-panel";
import { PageHeader } from "../../components/page/page-header";
import { MarksTable } from "../../components/records/marks-table";
import { recordMarksQuery, recordsListQuery, useRecordWrites } from "../../components/records/queries";
import { RecordDrawer } from "../../components/records/record-drawer";
import { MarksFilters, RecordsFilters } from "../../components/records/records-filters";
import { RecordsTable } from "../../components/records/records-table";
import {
	type BookDraft,
	EMPTY_BOOK_DRAFT,
	EMPTY_MARKS_DRAFT,
	type MarksDraft,
	RECORDS_TABS,
	type RecordsSearch,
	type RecordsTab,
	bookDraftFrom,
	bookFilterCount,
	bookPaging,
	bookSearchFrom,
	marksDraftFrom,
	marksFilterCount,
	marksParams,
	marksSearchFrom,
	recordsParams,
	recordsSearch,
} from "../../components/records/search";
import { Button } from "../../components/ui/button";
import { ConfirmDialog } from "../../components/ui/confirm-dialog";
import { EmptyState } from "../../components/ui/empty-state";
import { Spinner } from "../../components/ui/spinner";
import { toastApiError, toastSuccess } from "../../components/ui/toast";
import { errorMessage } from "../../lib/api/messages";
import { gamesSportsQuery, useCurrentGames } from "../../lib/games";
import { formatInteger } from "../../lib/i18n/format";

/**
 * Results › Records — the old OVR's Records page. Two tabs:
 *
 *   Record book         the games' records (`GET /records`): of an event of
 *                       the games, or of an event they do not run; the
 *                       setup roles add, edit and delete them
 *   Set at these games  results the meet screens marked GR / NR…
 *                       (`GET /records/marks`), so the book can be updated
 *
 * Filters and the tab live in the URL; switching games in the sidebar drops
 * the sport / discipline / event filters (they belong to the old games).
 */
export const Route = createFileRoute("/_app/records")({
	validateSearch: recordsSearch,
	component: RecordsPage,
});

const canWriteBook = (role: RoleCode | null | undefined) => role != null && (COMPETITION_SETUP_ROLES as readonly RoleCode[]).includes(role);

function RecordsPage() {
	const { t } = useTranslation("records");
	const search = Route.useSearch();
	const { session } = Route.useRouteContext();
	const navigate = useNavigate({ from: "/records" });
	const current = useCurrentGames();
	const gamesId = current.gamesId;
	const tab: RecordsTab = search.tab ?? "book";

	const go = useCallback((next: RecordsSearch, replace = false) => void navigate({ search: next, replace }), [navigate]);
	useOnGamesSwitch(gamesId, () =>
		go({ ...search, sport: undefined, discipline: undefined, event: undefined, page: undefined, msport: undefined, mpage: undefined }, true),
	);

	const sports = useQuery({ ...gamesSportsQuery(gamesId ?? ""), enabled: gamesId !== null });

	if (current.error) {
		return (
			<div className="flex flex-col">
				<PageHeader title={t("title")} />
				<EmptyState title={t("errors.gamesTitle")} body={errorMessage(current.error)} />
			</div>
		);
	}
	if (current.isLoading) return <Spinner className="size-[1.5rem] text-board" />;
	if (!current.games || gamesId === null) {
		return (
			<div className="flex flex-col">
				<PageHeader title={t("title")} />
				<EmptyState title={t("empty.noGamesTitle")} body={t("empty.noGamesBody")} />
			</div>
		);
	}

	const tabButton = (k: RecordsTab) => (
		<button
			key={k}
			type="button"
			role="tab"
			aria-selected={tab === k}
			onClick={() => go({ ...search, tab: k === "book" ? undefined : k })}
			className={`inline-flex h-[2.5rem] cursor-pointer items-center px-[1.125rem] font-bold text-[0.9375rem] pointer-coarse:h-touch ${
				tab === k ? "bg-board text-white" : "bg-paper text-muted hover:text-ink"
			}`}
		>
			{t(`tabs.${k}`)}
		</button>
	);

	return (
		<div className="flex flex-col">
			<PageHeader title={t("title")} subtitle={t("subtitle", { games: current.games.name })} />
			<div className="flex flex-col gap-[1rem]">
				<div role="tablist" aria-label={t("tabs.label")} className="inline-flex self-start overflow-hidden rounded-btn ring-1 ring-rule">
					{RECORDS_TABS.map(tabButton)}
				</div>
				{tab === "book" ? (
					<BookTab
						key={gamesId}
						gamesId={gamesId}
						gamesName={current.games.name}
						search={search}
						go={go}
						sports={sports.data ?? []}
						canWrite={canWriteBook(session.active_role)}
					/>
				) : (
					<MarksTab key={gamesId} gamesId={gamesId} search={search} go={go} sports={sports.data ?? []} />
				)}
			</div>
		</div>
	);
}

type Go = (next: RecordsSearch, replace?: boolean) => void;
type Sports = readonly SportOption[];

function BookTab({
	gamesId,
	gamesName,
	search,
	go,
	sports,
	canWrite,
}: {
	gamesId: string;
	gamesName: string;
	search: RecordsSearch;
	go: Go;
	sports: Sports;
	canWrite: boolean;
}) {
	const { t } = useTranslation("records");
	const applied = useMemo(() => bookDraftFrom(search), [search]);
	const onApply = useCallback((d: BookDraft) => go(bookSearchFrom(d, search)), [go, search]);
	const f = useFilterDraft<BookDraft>(applied, onApply, EMPTY_BOOK_DRAFT);
	const filterCount = bookFilterCount(search);

	const params = recordsParams(gamesId, search);
	const list = useQuery({ ...recordsListQuery(params), placeholderData: keepPreviousData });
	const { page, pageSize } = bookPaging(search);
	const beyond = list.data && list.data.items.length === 0 ? pageBeyondEnd(page, list.data.total, pageSize) : null;
	useEffect(() => {
		if (beyond !== null) go({ ...search, page: pageToUrl(beyond) }, true);
	}, [beyond]);

	const top = useRef<HTMLDivElement>(null);
	useScrollToTopOnPage(top, page);

	// The drawer: null = closed; "new" = add; a record = edit.
	const [drawer, setDrawer] = useState<EventRecordItem | "new" | null>(null);
	const [deleting, setDeleting] = useState<EventRecordItem | null>(null);
	const { remove } = useRecordWrites();

	const total = list.data?.total ?? 0;
	const rows = list.data?.items ?? [];

	const confirmDelete = async () => {
		if (!deleting) return;
		try {
			await remove.mutateAsync(deleting.event_record_id);
			toastSuccess(t("delete.done"));
			setDeleting(null);
		} catch (e) {
			toastApiError(e);
		}
	};

	return (
		<>
			<FilterPanel activeCount={filterCount}>
				<RecordsFilters gamesId={gamesId} draft={f.draft} set={f.set} onApply={f.apply} onReset={f.reset} activeCount={filterCount} sports={sports} />
			</FilterPanel>

			<div ref={top} className="scroll-mt-[4.5rem]">
				<RecordsTable
					rows={rows}
					loading={list.isPending || (list.isFetching && list.isPlaceholderData)}
					error={list.isError ? { message: errorMessage(list.error), onRetry: () => void list.refetch() } : null}
					empty={
						filterCount > 0
							? {
									title: t("empty.filteredTitle"),
									description: t("empty.filteredBody"),
									action: (
										<Button variant="outline" size="sm" onClick={f.reset}>
											{t("empty.reset")}
										</Button>
									),
								}
							: { title: t("empty.noneTitle", { games: gamesName }), description: canWrite ? t("empty.noneBodyWriter") : t("empty.noneBody") }
					}
					toolbar={
						list.data ? (
							<span className="text-[0.8125rem] text-muted" aria-live="polite">
								{t("table.count", { count: total, n: formatInteger(total) })}
							</span>
						) : null
					}
					toolbarEnd={
						canWrite ? (
							<Button size="sm" onClick={() => setDrawer("new")}>
								<svg viewBox="0 0 24 24" className="size-[1.125rem]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
									<path d="M12 5v14M5 12h14" />
								</svg>
								{t("actions.add")}
							</Button>
						) : undefined
					}
					pagination={
						list.data && total > 0
							? {
									page,
									pageSize,
									total,
									onPageChange: (p) => go({ ...search, page: pageToUrl(p) }),
									onPageSizeChange: (s) => go({ ...search, size: sizeToUrl(s, EVENT_RECORDS_PAGE_SIZE_DEFAULT), page: undefined }),
									pageSizeOptions: PAGE_SIZE_CHOICES,
								}
							: undefined
					}
					canWrite={canWrite}
					onEdit={(r) => setDrawer(r)}
					onDelete={(r) => setDeleting(r)}
				/>
			</div>

			{canWrite && drawer !== null && (
				<RecordDrawer
					gamesId={gamesId}
					sports={sports}
					record={drawer === "new" ? null : drawer}
					start={{ sportId: search.sport ?? "", disciplineId: search.sport ? (search.discipline ?? "") : "", eventId: search.sport ? (search.event ?? "") : "" }}
					onClose={() => setDrawer(null)}
				/>
			)}

			<ConfirmDialog
				open={deleting !== null}
				title={t("delete.title")}
				body={deleting ? t("delete.body", { type: deleting.record_type, holder: deleting.holder_name, event: deleting.event?.name ?? deleting.event_label ?? "" }) : undefined}
				confirmLabel={t("delete.confirm")}
				tone="danger"
				pending={remove.isPending}
				onConfirm={() => void confirmDelete()}
				onCancel={() => setDeleting(null)}
			/>
		</>
	);
}

function MarksTab({ gamesId, search, go, sports }: { gamesId: string; search: RecordsSearch; go: Go; sports: Sports }) {
	const { t } = useTranslation("records");
	const applied = useMemo(() => marksDraftFrom(search), [search]);
	const onApply = useCallback((d: MarksDraft) => go(marksSearchFrom(d, search)), [go, search]);
	const f = useFilterDraft<MarksDraft>(applied, onApply, EMPTY_MARKS_DRAFT);
	const filterCount = marksFilterCount(search);

	const params = marksParams(gamesId, search);
	const list = useQuery({ ...recordMarksQuery(params), placeholderData: keepPreviousData });
	const total = list.data?.total ?? 0;

	return (
		<>
			<FilterPanel activeCount={filterCount}>
				<MarksFilters draft={f.draft} set={f.set} onApply={f.apply} onReset={f.reset} activeCount={filterCount} sports={sports} />
			</FilterPanel>
			<MarksTable
				rows={list.data?.items ?? []}
				loading={list.isPending || (list.isFetching && list.isPlaceholderData)}
				error={list.isError ? { message: errorMessage(list.error), onRetry: () => void list.refetch() } : null}
				empty={{ title: t("marks.emptyTitle"), description: filterCount > 0 ? t("empty.filteredBody") : t("marks.emptyBody") }}
				toolbar={
					list.data ? (
						<span className="text-[0.8125rem] text-muted" aria-live="polite">
							{t("marks.count", { count: total, n: formatInteger(total) })}
						</span>
					) : null
				}
				pagination={
					list.data && total > 0
						? {
								page: params.page,
								pageSize: params.page_size,
								total,
								onPageChange: (p) => go({ ...search, mpage: pageToUrl(p) }),
							}
						: undefined
				}
			/>
		</>
	);
}
